/**
 * Coups de pouce — attendu 620 (fractions : quotient, valeur décimale, égalités).
 *
 * Routage par generator_key SEUL (pas de sousType : chaque clé de format
 * appartient à exactement une carte). Livraison incrémentale (revue
 * 2026-08-01) : FICHE_QUOTIENT d'abord, les deux autres cartes suivront.
 * Valeurs d'exemples HORS pools : 75 > 60 (les tirages plafonnent à 60).
 */

// Le geste positionnel ANIMÉ : « 75 ÷ 8 » — le nombre qu'on divise vole en
// haut de la fraction, celui qui divise vole en bas, ✓. Même motif CSS que
// les animations 650-652 (fenêtres d'opacité, fallback statique).
const SVG_QUOTIENT = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 148" width="290" height="143" role="img"
     aria-label="75 divisé par 8 : le nombre qu'on divise, le numérateur 75, va en haut de la fraction ; le nombre qui divise, le dénominateur 8, va en bas ; la fraction 75 sur 8 est le résultat exact de la division">
  <style>
    .anim { opacity: 1; }
    @media (prefers-reduced-motion: no-preference) {
      .q1 { opacity: 0; animation: cp620quo 5.4s linear infinite 0s; }
      .q2 { opacity: 0; animation: cp620quo 5.4s linear infinite 1.8s; }
      .q3 { opacity: 0; animation: cp620quo 5.4s linear infinite 3.6s; }
    }
    @keyframes cp620quo { 0%, 5% { opacity: 0; } 12%, 90% { opacity: 1; } 96%, 100% { opacity: 0; } }
  </style>
  <text x="40" y="81" font-size="17" font-weight="bold" fill="#0f172a">75</text>
  <text x="72" y="81" font-size="17" fill="#0f172a">÷</text>
  <text x="95" y="81" font-size="17" font-weight="bold" fill="#0f172a">8</text>
  <line x1="200" y1="74" x2="252" y2="74" stroke="#0f172a" stroke-width="2.5"/>
  <g class="anim q1">
    <path d="M 62 62 Q 120 22 205 50" fill="none" stroke="#f97316" stroke-width="2.5"/>
    <polygon points="212,54 202,44 200,55" fill="#f97316"/>
    <text x="130" y="12" font-size="11" fill="#64748b">le nombre qu'on divise :<tspan x="130" dy="13" font-weight="bold" fill="#f97316">le numérateur</tspan></text>
    <text x="226" y="66" text-anchor="middle" font-size="16" font-weight="bold" fill="#f97316">75</text>
  </g>
  <g class="anim q2">
    <path d="M 104 92 Q 150 128 205 100" fill="none" stroke="#fb923c" stroke-width="2.5"/>
    <polygon points="212,96 201,94 204,105" fill="#fb923c"/>
    <text x="118" y="124" font-size="11" fill="#64748b">le nombre qui divise :<tspan x="118" dy="13" font-weight="bold" fill="#fb923c">le dénominateur</tspan></text>
    <text x="226" y="96" text-anchor="middle" font-size="16" font-weight="bold" fill="#fb923c">8</text>
  </g>
  <g class="anim q3">
    <text x="266" y="81" font-size="16" font-weight="bold" fill="#f97316">✓</text>
  </g>
</svg>`.trim();

const FICHE_QUOTIENT = {
    pages: [
        {
            titre: 'La fraction est une division',
            lignes: [
                'Écrire $\\dfrac{a}{b}$, c\'est écrire le résultat **exact** de a ÷ b, même quand la division ne tombe pas juste.',
                'Le nombre qu\'on **divise** va en **haut**, le nombre **qui divise** va en **bas**.',
            ],
        },
        {
            // SVG quasi seul : le schéma porte le geste positionnel
            titre: 'Un exemple',
            svg: SVG_QUOTIENT,
        },
    ],
};

// La division décimale POSÉE, animée (le geste du programme : « il constate
// que 3/4 = 0,75 en posant la division ») : 61 ÷ 5 — quotient 12, reste 1,
// on POSE LA VIRGULE et on abaisse un 0, 10 ÷ 5 = 2, reste 0 : 12,2.
// Valeurs hors pools (numérateur > 60).
const SVG_DIVISION_DECIMALE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 206" width="290" height="199" role="img" data-etapes="8"
     aria-label="Division posée de 61 par 5 : dans 6, il y a 1 fois 5, il reste 1 ; on abaisse le 1, ça fait 11 ; dans 11, il y a 2 fois 5, il reste 1 ; plus de chiffre et le reste n’est pas zéro ; or 61 c’est 61,0 : on pose la virgule au quotient ; on abaisse ce zéro, ça fait 10 ; dans 10, il y a 2 fois 5, il reste 0 ; c’est fini : 61 divisé par 5 égale 12,2">
  <style>
    .anim { opacity: 1; }
    [data-caption] { opacity: 0; }
    [data-caption="8"] { opacity: 1; }
    @media (prefers-reduced-motion: no-preference) {
      .e1 { opacity: 0; animation: cp620dec 16.0s linear infinite 0.0s; }
      .c1 { opacity: 0; animation: cp620cap 16.0s linear infinite 0.0s; }
      .e2 { opacity: 0; animation: cp620dec 16.0s linear infinite 2.0s; }
      .c2 { opacity: 0; animation: cp620cap 16.0s linear infinite 2.0s; }
      .e3 { opacity: 0; animation: cp620dec 16.0s linear infinite 4.0s; }
      .c3 { opacity: 0; animation: cp620cap 16.0s linear infinite 4.0s; }
      .e4 { opacity: 0; animation: cp620dec 16.0s linear infinite 6.0s; }
      .c4 { opacity: 0; animation: cp620cap 16.0s linear infinite 6.0s; }
      .e5 { opacity: 0; animation: cp620dec 16.0s linear infinite 8.0s; }
      .c5 { opacity: 0; animation: cp620cap 16.0s linear infinite 8.0s; }
      .e6 { opacity: 0; animation: cp620dec 16.0s linear infinite 10.0s; }
      .c6 { opacity: 0; animation: cp620cap 16.0s linear infinite 10.0s; }
      .e7 { opacity: 0; animation: cp620dec 16.0s linear infinite 12.0s; }
      .c7 { opacity: 0; animation: cp620cap 16.0s linear infinite 12.0s; }
      .e8 { opacity: 0; animation: cp620dec 16.0s linear infinite 14.0s; }
      .c8 { opacity: 0; animation: cp620capfin 16.0s linear infinite 14.0s; }
    }
    @keyframes cp620dec { 0%, 2% { opacity: 0; } 5%, 95% { opacity: 1; } 98%, 100% { opacity: 0; } }
    @keyframes cp620cap { 0%, 2% { opacity: 0; } 4%, 11% { opacity: 1; } 13%, 100% { opacity: 0; } }
    @keyframes cp620capfin { 0%, 2% { opacity: 0; } 5%, 95% { opacity: 1; } 98%, 100% { opacity: 0; } }
  </style>
  <text x="48" y="30" text-anchor="middle" font-size="16" font-weight="bold" fill="#0f172a">6</text>
  <text x="62" y="30" text-anchor="middle" font-size="16" font-weight="bold" fill="#0f172a">1</text>
  <line x1="85" y1="14" x2="85" y2="172" stroke="#0f172a" stroke-width="2"/>
  <line x1="85" y1="36" x2="160" y2="36" stroke="#0f172a" stroke-width="2"/>
  <text x="95" y="30" font-size="16" fill="#0f172a">5</text>
  <g class="anim e1" data-etape="1">
    <circle cx="48" cy="25" r="11" fill="none" stroke="#f97316" stroke-width="1.5"/>
    <text x="95" y="58" font-size="16" font-weight="bold" fill="#f97316">1</text>
    <text x="54" y="52" text-anchor="end" font-size="13" fill="#0f172a">− 5</text>
    <line x1="36" y1="58" x2="58" y2="58" stroke="#0f172a" stroke-width="1.5"/>
    <text x="48" y="74" text-anchor="middle" font-size="14" font-weight="bold" fill="#0f172a">1</text>
  </g>
  <g class="anim e2" data-etape="2">
    <line x1="62" y1="36" x2="62" y2="62" stroke="#fb923c" stroke-width="1.5" stroke-dasharray="3 2"/>
    <polygon points="62,66 58,58 66,58" fill="#fb923c"/>
    <text x="62" y="74" text-anchor="middle" font-size="14" font-weight="bold" fill="#fb923c">1</text>
  </g>
  <g class="anim e3" data-etape="3">
    <text x="106" y="58" font-size="16" font-weight="bold" fill="#f97316">2</text>
    <text x="66" y="94" text-anchor="end" font-size="13" fill="#0f172a">− 10</text>
    <line x1="36" y1="100" x2="70" y2="100" stroke="#0f172a" stroke-width="1.5"/>
    <text x="62" y="118" text-anchor="middle" font-size="14" font-weight="bold" fill="#0f172a">1</text>
  </g>
  <g class="anim e4" data-etape="4">
    <circle cx="62" cy="113" r="10" fill="none" stroke="#dc2626" stroke-width="2"/>
  </g>
  <g class="anim e5" data-etape="5">
    <text x="69" y="30" font-size="16" font-weight="bold" fill="#dc2626">,</text>
    <text x="79" y="30" text-anchor="middle" font-size="16" font-weight="bold" fill="#dc2626">0</text>
    <text x="115" y="58" font-size="17" font-weight="bold" fill="#dc2626">,</text>
  </g>
  <g class="anim e6" data-etape="6">
    <line x1="79" y1="36" x2="79" y2="106" stroke="#dc2626" stroke-width="1.5" stroke-dasharray="3 2"/>
    <polygon points="79,110 75,102 83,102" fill="#dc2626"/>
    <text x="76" y="118" text-anchor="middle" font-size="14" font-weight="bold" fill="#dc2626">0</text>
  </g>
  <g class="anim e7" data-etape="7">
    <text x="124" y="58" font-size="16" font-weight="bold" fill="#f97316">2</text>
    <text x="80" y="140" text-anchor="end" font-size="13" fill="#0f172a">− 10</text>
    <line x1="36" y1="146" x2="84" y2="146" stroke="#0f172a" stroke-width="1.5"/>
    <text x="76" y="164" text-anchor="middle" font-size="14" font-weight="bold" fill="#0f172a">0</text>
  </g>
  <g class="anim e8" data-etape="8">
    <circle cx="76" cy="159" r="10" fill="none" stroke="#f97316" stroke-width="1.5"/>
  </g>
  <g class="anim c1" data-caption="1">
    <text x="150" y="196" text-anchor="middle" font-size="12" fill="#334155">dans 6, il y a <tspan font-weight="bold" fill="#f97316">1 fois 5</tspan>, il reste 1</text>
  </g>
  <g class="anim c2" data-caption="2">
    <text x="150" y="196" text-anchor="middle" font-size="12" fill="#334155">on <tspan font-weight="bold" fill="#fb923c">abaisse le 1</tspan> : ça fait 11</text>
  </g>
  <g class="anim c3" data-caption="3">
    <text x="150" y="196" text-anchor="middle" font-size="12" fill="#334155">dans 11, il y a <tspan font-weight="bold" fill="#f97316">2 fois 5</tspan>, il reste 1</text>
  </g>
  <g class="anim c4" data-caption="4">
    <text x="150" y="196" text-anchor="middle" font-size="12" fill="#334155">plus de chiffre, et le <tspan font-weight="bold" fill="#dc2626">reste n’est pas 0</tspan></text>
  </g>
  <g class="anim c5" data-caption="5">
    <text x="150" y="196" text-anchor="middle" font-size="12" fill="#334155">61 = <tspan font-weight="bold" fill="#dc2626">61,0</tspan> : on pose la <tspan font-weight="bold" fill="#dc2626">virgule</tspan></text>
  </g>
  <g class="anim c6" data-caption="6">
    <text x="150" y="196" text-anchor="middle" font-size="12" fill="#334155">on <tspan font-weight="bold" fill="#dc2626">abaisse ce 0</tspan> : ça fait 10</text>
  </g>
  <g class="anim c7" data-caption="7">
    <text x="150" y="196" text-anchor="middle" font-size="12" fill="#334155">dans 10, il y a <tspan font-weight="bold" fill="#f97316">2 fois 5</tspan>, il reste 0</text>
  </g>
  <g class="anim c8" data-caption="8">
    <text x="150" y="196" text-anchor="middle" font-size="12" fill="#334155">reste 0, fini : 61 ÷ 5 = <tspan font-weight="bold" fill="#f97316">12,2</tspan> ✓</text>
  </g>
</svg>`.trim();

const FICHE_DECIMALE = {
    pages: [
        {
            titre: 'Diviser pour trouver la valeur',
            lignes: [
                'La valeur décimale de $\\dfrac{a}{b}$, c\'est le résultat de la division **a ÷ b** : pose-la.',
                'Attention : $\\dfrac{3}{4}$ ne vaut pas ==3,4== ! Il faut vraiment faire la division.',
            ],
        },
        {
            titre: 'Un exemple posé',
            lignes: [
                'Prends un **brouillon** et un stylo : pose la division en même temps que la figure !',
            ],
            svg: SVG_DIVISION_DECIMALE,
        },
    ],
};

// Carte « Égalités à trou » : deux gestes INVERSES de la même propriété
// dividende = diviseur × quotient, en MOTS (les lettres arrivent en 5e).
// Routage direct par clé : les deux formats « num » → fiche numérateur,
// egalite_trou_texte (100 % dénominateur depuis l'arbitrage 2026-08-01)
// → fiche dénominateur. Valeurs hors pools : b = 8 (num), et 12/? = 2 →
// réponse 6 : le diviseur 6 n'est pas dans le pool {2,3,4,5,10}.
const FICHE_EGALITE_NUM = {
    pages: [
        {
            titre: 'Le numérateur manquant',
            lignes: [
                'Dans une fraction, le nombre du **haut** est le **dividende**, celui du **bas** le **diviseur**, et le résultat le **quotient**.',
                'Or **dividende = diviseur × quotient**.',
                'Exemple : $\\dfrac{?}{8} = 1,5$ → le dividende manquant vaut 8 × 1,5 = **12**. Vérifie : 12 ÷ 8 = 1,5 ✓',
            ],
        },
    ],
};

const FICHE_EGALITE_DEN = {
    pages: [
        {
            titre: 'Le dénominateur manquant',
            lignes: [
                'Ici c\'est le **diviseur** (le nombre du bas) qui manque, et toujours **dividende = diviseur × quotient**.',
                'Cherche donc le nombre qui, multiplié par le **quotient**, donne le **dividende**.',
                'Exemple : $\\dfrac{12}{?} = 2$ : comme **6** × 2 = 12, le diviseur manquant est **6**.',
            ],
        },
    ],
};

const CP_620 = {
    fraction_quotient_qcm: FICHE_QUOTIENT,
    fraction_quotient_texte: FICHE_QUOTIENT,
    valeur_decimale_qcm: FICHE_DECIMALE,
    valeur_decimale_texte: FICHE_DECIMALE,
    valeur_decimale_relier: FICHE_DECIMALE,
    valeur_decimale_vf: FICHE_DECIMALE,
    egalite_num_texte: FICHE_EGALITE_NUM,
    egalite_num_trou_saisie: FICHE_EGALITE_NUM,
    egalite_trou_texte: FICHE_EGALITE_DEN,
};

export default CP_620;
